import { mutateInstantBrandAI } from "@/Services";
import { Box, Button, Center, Group, Image, Modal, SegmentedControl, Text, TextInput } from "@mantine/core";
import { randomId, useDisclosure, useSetState } from "@mantine/hooks";
import { IconBrandFacebook, IconBrandInstagram, IconBrandLinkedin, IconBrandYoutube, IconSparkles, IconWorld } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";

import { icon_ai_main, icon_ai_search, icon_ai_eye, icon_ai_image, icon_ai_pencil, icon_ai_text, icon_ai_video, icon_ai_success, icon_ai_failed } from "@/Components/ui";
import { useStudioFormContext } from "@/Pages/Studio/AdBuilder/form-context";
import { mergeGallerySuggestions, useAdBuilder } from "@/Pages/Studio/AdBuilder/adbuilder-context";
import { useTranslation } from "react-i18next";

// The agent keys slogans `slide_1`, `slide_2`…; one list per slide, in slide order.
const slideSlogans = (slogans) => Object.keys(slogans)
    .filter((key) => key.startsWith('slide_'))
    .sort((a, b) => parseInt(a.split('_')[1]) - parseInt(b.split('_')[1]))
    .map((slideKey) => slogans[slideKey] || []);

const aiExtracted = (urls, kind) => (urls || []).filter((url) => url.includes(`AI_EXTRACT_${kind}`));

export const InstantAIAction = ({ template = 'post' }) => {

    const { t, i18n } = useTranslation();
    const form = useStudioFormContext();
    const { aiSuggestions, setAiSuggestions, setActiveTabSlide } = useAdBuilder();

    const isFirstOpenRef = useRef(true);
    // AD-105: guard against duplicate concurrent brand calls (the deep-link
    // effect and the lang watcher can both fire on load, racing hasError and
    // wasting scrape credits). Only one call runs at a time.
    const inFlightRef = useRef(false);

    const platform = {
        'website': {
            title: t('ai.website.title'),
            subTitle: t('ai.website.subtitle'),
            label: t('adBuilder.websiteUrl'),
            placeholder: t('ai.website.placeholder'),
            icon: <IconWorld />,
            buttonText: t('ai.website.button'),
            buttonTextMain: t('ai.website.button'),
        },
        'youtube': {
            title: t('ai.youtube.title'),
            subTitle: t('ai.youtube.subtitle'),
            label: t('ai.youtube.label'),
            placeholder: t('ai.youtube.placeholder'),
            icon: <IconBrandYoutube />,
            buttonText: t('ai.youtube.button'),
            buttonTextMain: t('ai.youtube.button'),
        },
        'facebook': {
            title: t('ai.social.title'),
            subTitle: t('ai.social.subtitle'),
            label: t('ai.social.facebookLabel'),
            placeholder: t('ai.social.placeholder'),
            icon: <IconBrandFacebook />,
            buttonText: t('ai.social.button'),
            buttonTextMain: t('ai.social.title')
        },
        'linkedin': {
            title: t('ai.social.title'),
            subTitle: t('ai.social.subtitle'),
            label: t('ai.social.linkedinLabel'),
            placeholder: t('ai.social.placeholder'),
            icon: <IconBrandLinkedin />,
            buttonText: t('ai.social.button'),
            buttonTextMain: t('ai.social.title')
        },
        'instagram': {
            title: t('ai.social.title'),
            subTitle: t('ai.social.subtitle'),
            label: t('ai.social.instagramLabel'),
            placeholder: t('ai.social.placeholder'),
            icon: <IconBrandInstagram />,
            buttonText: t('ai.social.button'),
            buttonTextMain: t('ai.social.title')
        },
    }

    const SOCIAL_ALIASES = [
        'bannerset-halfpage-post',
        'bannerset-halfpage-post-full',
        'bannerset-halfpage-post-full-slogan',
        'bannerset-halfpage-post-half',
        'bannerset-halfpage-post-half-slogan',
        'story-ad-3-halfpage-full',
        'story-ad-3-halfpage-full-slogan',
        'story-ad-3-halfpage-half',
        'story-ad-3-halfpage-half-slogan',
        // Medium Rectangle social layouts. Without these resolvePlatform()
        // returned 'website' at mount for an MR social ad, and the watcher that
        // corrects the platform from clickout_url never ran — so the AI applied
        // the website handler and wiped the caption.
        'story-ad-1-full',
        'story-ad-1-full-slogan',
        'story-ad-1-half',
        'story-ad-1-half-slogan',
        'story-ad-1-full-post-full',
        'story-ad-1-full-post-half',
        'story-ad-1-full-slogan-post-full',
        'story-ad-1-full-slogan-post-half',
    ];

    const resolvePlatform = (template) => {
        // AD-105: the template name doesn't identify the social network — the
        // post URL does. Detect from the URL first, template as fallback.
        const site = (form.getValues()?.website || '').toLowerCase();
        if (site.includes('instagram.com')) return 'instagram';
        if (site.includes('facebook.com')) return 'facebook';
        if (site.includes('linkedin.com')) return 'linkedin';
        if (site.includes('youtube.com') || site.includes('youtu.be')) return 'youtube';
        // Any other real URL is a plain website — template heuristics below
        // only apply when no URL is available yet.
        if (site.startsWith('http')) return 'website';
        if (template?.includes('video')) return 'youtube';
        if (SOCIAL_ALIASES.includes(template)) return 'instagram';
        return 'website';
    };

    const [opened, { open, close }] = useDisclosure(false);

    const [params, setParams] = useSetState({
        id: route().params.ad,
        website: form.getValues()?.website,
        platform: resolvePlatform(template),
        template: template?.includes("video") ? 'video' : template?.includes("swipe") ? 'swipe' : template?.includes("slider") ? 'slider' : 'post',
    });

    // AD-105: always-current params — the auto-trigger runs from a setTimeout
    // whose closures captured the FIRST render's params (website null). Refs
    // are read at call time, so every consumer sees the latest values.
    const paramsRef = useRef(params);
    paramsRef.current = params;

    // Always-current aiSuggestions (localStorage hydration lands after mount).
    const aiSuggestionsRef = useRef(aiSuggestions);
    aiSuggestionsRef.current = aiSuggestions;

    // aiSuggestions persists in localStorage — never resurrect a stale
    // 'loading' state from a previous session.
    useEffect(() => {
        // Delay past the useLocalStorage hydration: writing during mount would
        // persist the empty default and wipe the saved suggestions on refresh.
        const t = setTimeout(() => {
            const curr = aiSuggestionsRef.current;
            // Clear any stale loading states persisted by a previous session.
            if (curr?.slogansStatus === 'loading' || curr?.galleryStatus === 'loading') {
                setAiSuggestions((c) => ({
                    ...c,
                    slogansStatus: c?.slogansStatus === 'loading' ? undefined : c?.slogansStatus,
                    galleryStatus: c?.galleryStatus === 'loading' ? undefined : c?.galleryStatus,
                }));
            }
        }, 2500);
        return () => clearTimeout(t);
    }, []);

    const mutation = mutateInstantBrandAI({ ...params, language: form.getValues()?.lang || i18n.language });
    const [hasError, setHasError] = useState(false);

    // Without a suggestion the CTA must still read in the ad's language, not English.
    const applyCta = (cta) => {
        if (!form.getValues()?.more_btn) {
            form.setFieldValue('more_btn', cta || t('ai.defaultCta', { lng: form.getValues()?.lang || i18n.language }));
        }
    };

    // AD-105 async enrichment: AI slogans/CTA are generated in the background
    // after the initial (sync) render and applied when ready. Non-blocking —
    // any failure/timeout simply leaves the editor without AI slogans.
    const applySlogans = (slogans) => {
        if (!slogans || Array.isArray(slogans)) {
            setAiSuggestions((curr) => ({ ...curr, slogansStatus: 'empty' }));
            applyCta();
            return;
        }

        const formatedSlogans = slideSlogans(slogans);

        if (!formatedSlogans.length && !slogans.cta_buttons) {
            setAiSuggestions((curr) => ({ ...curr, slogansStatus: 'empty' }));
            applyCta();
            return;
        }

        setAiSuggestions((curr) => ({
            ...curr,
            slogansStatus: 'ready',
            slogans: formatedSlogans,
            subslogans: formatedSlogans,
            cta_buttons: slogans.cta_buttons || [],
        }));

        // Fill the CTA only if the user hasn't set one (never overwrite edits).
        applyCta(slogans.cta_buttons?.[0]);

        // Fill empty slide slogans with the first suggestion for that slide.
        const slides = form.getValues()?.slides || [];
        slides.forEach((slide, index) => {
            if (!slide?.slogan && formatedSlogans[index]?.[0]) {
                form.setFieldValue(`slides.${index}.slogan`, formatedSlogans[index][0]);
            }
        });

        setActiveTabSlide('ai');
        form.saveAndPreview();
    };

    const loadSlogansAsync = (data) => {
        // AD-105: visible pending state for the AI Suggestions area only.
        setAiSuggestions((curr) => ({ ...curr, slogansStatus: 'loading' }));
        axios.get(route('studio.v1.api.instant.brand.show'), {
            params: {
                id: route().params.ad,
                website: data?.url || paramsRef.current.website,
                platform: paramsRef.current.platform,
                language: form.getValues()?.lang || i18n.language,
                post_caption: data?.post_caption || '',
                slogansOnly: 1,
            },
        })
            .then((res) => applySlogans(res?.data))
            .catch(() => {
                // Editor stays usable — just surface the failure in the area.
                setAiSuggestions((curr) => ({ ...curr, slogansStatus: 'error' }));
                applyCta();
            });
    };


    const getInstantBrand = () => {

        if (inFlightRef.current) return;
        inFlightRef.current = true;

        setHasError(false);
        // AD-105 measurement: timing log to check scrape/AI latency.
        const __loadStart = performance.now();

        mutation.mutate({}, {
            onSuccess: ({ data }) => {

                inFlightRef.current = false;
                const __loadMs = Math.round(performance.now() - __loadStart);
                console.log(
                    `%c[AD-105] ${params.platform} load: ${(__loadMs / 1000).toFixed(2)}s` +
                    (data?._elapsed_ms != null ? ` (server ${(data._elapsed_ms / 1000).toFixed(2)}s)` : ''),
                    'background:#111;color:#0f0;font:14px monospace;padding:4px 8px;border-radius:4px;'
                );

                setTimeout(close, isFirstOpenRef.current ? 1000 : 2500)


                if (Array.isArray(data) && data.length === 0) {
                    setHasError(true);
                    return;
                }

                if (params.platform == 'website') {
                    setDataForWebsiteForm(data);
                } else if (params.platform == 'youtube') {
                    setDataForvideoForm({ ...data, website: params.website });
                }
                else if (params.platform == 'instagram' || params.platform == 'facebook' || params.platform == 'linkedin') {
                    // AD-105: FB/LinkedIn have the same shape as Instagram
                    // (gallery + caption + favicon) — use the social handler so
                    // post_caption and up to 3 photo slides are applied.
                    setDataInstagramForm({ ...data, website: params.website });
                } else {
                    setDataForWebsiteForm(data);
                }

                form.saveAndPreview();
                setActiveTabSlide('ai');

                // Non-blocking: generate AI slogans/CTA in the background and
                // apply them to the editor once the AI server responds.
                loadSlogansAsync(data);

                isFirstOpenRef.current = false;
            },
            onError: () => {
                inFlightRef.current = false;
                setHasError(true);
            }
        });
    }

    // Brand colours, logo, favicon and CTA from an AI result; whatever it lacks keeps its value.
    const applyBrandValues = (data, logos, favicons, extra = {}) => form.setValues({
        color_1: data?.colors_selected?.button_color || form.getValues()?.color_1,
        color_2: data?.colors_selected?.background_color || form.getValues()?.color_2,
        color_3: data?.colors_selected?.slogan_color || form.getValues()?.color_3,
        color_4: data?.colors_selected?.button_text_color || form.getValues()?.color_4,
        logo: logos?.[0] || form.getValues()?.logo,
        favicon: data?.favicon || favicons?.[0] || logos?.[0] || form.getValues()?.favicon,
        more_btn: data?.slogans?.cta_buttons?.[0] || form.getValues()?.more_btn,
        ...extra,
    });

    const applyAiSuggestions = (data, formatedSlogans, logos, favicons, gallery) => setAiSuggestions(curr => ({
        ...curr,
        logos: logos,
        favicons: data?.favicon ? [data.favicon, ...favicons] : favicons,
        colors: ['#000000', '#ffffff', ...(data?.colors || [])],
        cta_buttons: data?.slogans?.cta_buttons || [],
        slogans: formatedSlogans || [],
        subslogans: formatedSlogans || [],
        gallery: mergeGallerySuggestions(curr?.gallery, gallery)
    }));

    const setDataForWebsiteForm = (data) => {

        const formatedSlogans = slideSlogans(data?.slogans || {});

        const gallery = data?.images?.images || [];
        const logos = aiExtracted(data?.images?.logo, 'logo');
        const favicons = aiExtracted(data?.images?.favicon, 'favicon');

        applyBrandValues(data, logos, favicons)

        applyAiSuggestions(data, formatedSlogans, logos, favicons, gallery)

        form.setFieldValue('slides', []);

        var maxSlides = 3;

        if (params.platform == 'facebook' || params.platform == 'linkedin') {
            maxSlides = 1;
        }

        var slides = [];

        // AD-105: slides are built from the gallery images (no slogans).
        gallery?.slice(0, maxSlides)?.forEach((image, index) => {
            slides.push({
                slogan: '',
                // This handler never supplies a caption, so it must not clear one
                // either: on a caption layout (-post-full / -post-half) the caption
                // is the only text that renders, and blanking it leaves the field
                // empty while images and slogans still show.
                additional_slogan: form.getValues()?.slides?.[index]?.additional_slogan || '',
                discounted_price: '',
                old_price: '',
                image: gallery?.[index] || gallery?.[0] || 0,
                focus_x: "0",
                focus_y: "0",
                key: randomId(),
                order: index + 1
            })
        })

        form.setFieldValue('slides', slides);
    }

    const setDataForvideoForm = (data) => {

        const formatedSlogans = slideSlogans(data?.slogans || {});

        const gallery = (data?.images?.images || []).filter(
            (url) => !url.includes("AI_EXTRACT_logo")
        );

        const logos = aiExtracted(data?.images?.logo, 'logo');

        const favicons = aiExtracted(data?.images?.favicon, 'favicon');

        applyBrandValues(data, logos, favicons, { video: data?.url || '' })

        // Slogan is blanked here (not carried over) so the async AI slogan
        // fill (applySlogans) actually replaces it once it arrives — matching
        // the website/Instagram flows, which rebuild `slides` from scratch.
        form.setFieldValue('slides.0', {
            ...(form.getValues()?.slides?.[0] || {}),
            slogan: '',
            additional_slogan: data?.post_caption || form.getValues()?.slides?.[0]?.additional_slogan || '',
            image: gallery?.[0] || form.getValues()?.slides?.[0]?.image || '',
        });

        applyAiSuggestions(data, formatedSlogans, logos, favicons, gallery)
    }

    const setDataInstagramForm = (data) => {

        console.log("Instagram Data:", data);

        const formatedSlogans = slideSlogans(data?.slogans || {});

        const gallery = (data?.images?.images || []).filter(
            (url) => !url.includes("AI_EXTRACT_logo")
        );

        const logos = aiExtracted(data?.images?.logo, 'logo');

        if(!gallery?.length){
            if(logos?.length){
                gallery.push(...logos);
            } else if (data?.post_images?.length) {
                // AD-105 provider validation: thin scraper responses carry raw
                // post image URLs instead of a processed gallery — use them so
                // slides and the caption still populate.
                gallery.push(...data.post_images.filter((u) => typeof u === 'string'));
            }
        }
        
        const favicons = aiExtracted(data?.images?.favicon, 'favicon');

        applyBrandValues(data, logos, favicons)

        applyAiSuggestions(data, formatedSlogans, logos, favicons, gallery)

        const maxSlides = Math.min(gallery?.length || 0, 3);
        const slides = form.getValues().slides ?? [];

        while (slides.length > maxSlides) {
            form.removeListItem('slides', slides.length - 1);
            slides.pop();
        }

        // AD-105: slides are built from the gallery images (no slogans).
        gallery?.slice(0, maxSlides)?.forEach((image, index) => {
            var slideObj = {
                slogan: '',
                additional_slogan: index == 0 ? data?.post_caption || '' : '',
                discounted_price: '',
                old_price: '',
                image: gallery?.[index] || gallery?.[0] || 0,
                focus_x: "0",
                focus_y: "0",
                key: randomId(),
                order: index + 1
            };

            if (form.getValues().slides?.[index]) {
                form.setFieldValue(`slides.${index}`, slideObj);
            }
            else {
                form.insertListItem(`slides`, slideObj);
            }

        })

        // AD-105: never lose the caption — apply it to the first slide even
        // when the response carried no usable images.
        if (!gallery?.length && data?.post_caption) {
            form.setFieldValue('slides.0.additional_slogan', data.post_caption);
        }
    }


    const openModal = () => {
        mutation.reset();
        setHasError(false);
        open();
    }

    function cleanWebsiteUrl() {

        const url = params.website;

        const trimmedUrl = url.trim();
        const urlPattern = /^(https?:\/\/)[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/\S*)?$/;
        const match = trimmedUrl.match(urlPattern);

        if (match) {
            let cleanedUrl = match[1] ? trimmedUrl : `https://${trimmedUrl}`;
            return cleanedUrl;
        } else {
            return null;
        }
    }

    useEffect(() => {
        if (route().params.urlToDisplay && route().params.platform) {
            setParams({
                website: route().params.urlToDisplay,
                platform: route().params.platform,
            });

            setTimeout(() => {
                openModal();
                getInstantBrand();
            }, 400);

            if (typeof window !== "undefined") {
                const url = new URL(window.location.href);
                url.searchParams.delete("urlToDisplay");
                url.searchParams.delete("platform");
                window.history.replaceState({}, "", url.toString());
            }
        }
    }, []);

    form.watch('lang', function () {
        if (['instagram', 'linkedin', 'facebook'].includes(params.platform)) {

            // AD-105: the form is loaded by now — derive the real website and
            // platform from the ad data instead of the stale mount-time params
            // (mount runs before the form has values, so params.website was
            // null and params.platform fell back to the template heuristic).
            const targetUrl = (form?.getValues()?.clickout_url?.length > 3
                ? form.getValues().clickout_url
                : form?.getValues()?.website) || params.website;
            const s = (targetUrl || '').toLowerCase();
            const detected = s.includes('instagram.com') ? 'instagram'
                : s.includes('facebook.com') ? 'facebook'
                : s.includes('linkedin.com') ? 'linkedin'
                : (s.includes('youtube.com') || s.includes('youtu.be')) ? 'youtube'
                : s.startsWith('http') ? 'website'
                : params.platform;

            setParams({
                website: targetUrl,
                platform: detected,
            });

            setTimeout(() => {
                openModal();
                getInstantBrand();
            }, 400);
        }

    });

    return (
        <>
            <Modal opened={opened}
                overlayProps={{
                    style: mutation.isPending
                        ? {
                            backdropFilter: 'blur(0px)',
                            backgroundSize: '400% 400%',
                            animation: 'aiGradient 10s ease infinite, aiBlur 4s ease-in-out infinite',
                            transition: 'all 1s ease',
                        }
                        : undefined,
                }}
                onClose={close} title={mutation.status == "idle" && (
                    <div>
                        <Text fw={600} fz={20}>{platform[params?.platform].title}</Text>
                        <Text fw={400} fz={16}>{platform[params?.platform].subTitle}</Text>
                    </div>)} radius="lg" size={660}>
                {

                    hasError ? (
                        <Box mb="lg">
                            <Center c="smartico-pink">
                                <Image src={icon_ai_failed} w={88} h={88} />
                            </Center>
                            <Center>
                                <Text fz={20} ta="center" fw={600} w={420}>{t('ai.errorTitle')}</Text>
                            </Center>
                            <Center mt="sm">
                                <Text fz={16} ta="center" fw={400} w={460}>{t('ai.errorSubtitle')}</Text>
                            </Center>
                        </Box>
                    ) : mutation.isSuccess ? (
                        <Box mb="lg">
                            <Center c="smartico-pink">
                                <Image src={icon_ai_success} w={88} h={88} />
                            </Center>
                            <Center>
                                <Text fz={20} ta="center" fw={600} w={420}>{t('ai.successTitle')}</Text>
                            </Center>
                            <Center mt="sm">
                                <Text fz={16} ta="center" fw={400} w={460}>{t('ai.successSubtitle')}</Text>
                            </Center>
                        </Box>
                    ) : mutation.isPending ? (
                        <LoadingAIAction type={params?.platform} />

                    ) : (
                        <>

                            {(params.platform !== 'website' && params.platform !== 'youtube') && (
                                <Box mb="md">
                                    <SegmentedControl
                                        value={params?.platform}
                                        onChange={(v) => setParams({ platform: v })}
                                        data={[
                                            {
                                                value: 'facebook',
                                                label: (
                                                    <Center style={{ gap: 6 }}>
                                                        <IconBrandFacebook size={18} />
                                                        <span>Facebook</span>
                                                    </Center>
                                                ),
                                            },
                                            {
                                                value: 'instagram',
                                                label: (
                                                    <Center style={{ gap: 6 }}>
                                                        <IconBrandInstagram size={18} />
                                                        <span>Instagram</span>
                                                    </Center>
                                                ),
                                            },

                                            {
                                                value: 'linkedin',
                                                label: (
                                                    <Center style={{ gap: 6 }}>
                                                        <IconBrandLinkedin size={18} />
                                                        <span>Linkedin</span>
                                                    </Center>
                                                ),
                                            },
                                        ]}
                                    />
                                </Box>
                            )}
                            <Box mb="md">
                                <TextInput
                                    label={platform[params?.platform].label}
                                    placeholder={platform[params?.platform].placeholder}
                                    radius="md"
                                    rightSection={platform[params?.platform].icon}
                                    onChange={(e) => {

                                        if (params.platform !== 'website' && params.platform !== 'youtube') {
                                            const url = e.currentTarget.value;

                                            if (url.includes('facebook.com')) {
                                                setParams({ platform: 'facebook' });
                                            } else if (url.includes('instagram.com')) {
                                                setParams({ platform: 'instagram' });
                                            } else if (url.includes('linkedin.com')) {
                                                setParams({ platform: 'linkedin' });
                                            }
                                        }

                                        return setParams({ website: e.currentTarget.value })
                                    }
                                    }
                                    error={cleanWebsiteUrl() === null && params.website.length > 0 ? t('ai.invalidUrl') : ''}
                                />
                                <Text fw={400} fz={12} color="gray">{t('ai.urlHint')}</Text>
                            </Box>
                            <Group mt="lg" justify="flex-end">

                                <Button onClick={close} variant="default" radius="md" size="xs">
                                    {t('common.cancel')}
                                </Button>
                                <Button leftSection={<IconSparkles />} radius="md" size="xs" color="smartico-pink" variant="filled" onClick={getInstantBrand} disabled={cleanWebsiteUrl() === null || params?.website?.length < 1}>
                                    {platform[params?.platform].buttonText}
                                </Button>
                            </Group>
                        </>
                    )}

            </Modal>

            <Button leftSection={<IconSparkles />} onClick={openModal} variant="default" radius="md">
                {platform[params?.platform].buttonTextMain}
            </Button>
        </>
    )
}


const LoadingAIAction = ({ type = "website" }) => {

    const { t } = useTranslation();
    const socialPlatforms = ["instagram", "facebook", "linkedin"];
    type = socialPlatforms.includes(type.toLowerCase()) ? "social" : type;

    const messages = {
        website: [
            {
                icon: icon_ai_main,
                title: t('ai.loading.website1.title'),
                sub: t('ai.loading.website1.subtitle')
            },
            {
                icon: icon_ai_search,
                title: t('ai.loading.website2.title'),
                sub: t('ai.loading.website2.subtitle')
            },
            {
                icon: icon_ai_eye,
                title: t('ai.loading.website3.title'),
                sub: t('ai.loading.website3.subtitle')
            },
            {
                icon: icon_ai_pencil,
                title: t('ai.loading.website4.title'),
                sub: t('ai.loading.website4.subtitle')
            },
            {
                icon: icon_ai_image,
                title: t('ai.loading.website5.title'),
                sub: t('ai.loading.website5.subtitle')
            },
            {
                icon: icon_ai_text,
                title: t('ai.loading.website6.title'),
                sub: t('ai.loading.website6.subtitle')
            }
        ],
        youtube: [
            {
                icon: icon_ai_video,
                title: t('ai.loading.youtube1.title'),
                sub: t('ai.loading.youtube1.subtitle')
            },
            {
                icon: icon_ai_eye,
                title: t('ai.loading.youtube2.title'),
                sub: t('ai.loading.youtube2.subtitle')
            },
            {
                icon: icon_ai_pencil,
                title: t('ai.loading.youtube3.title'),
                sub: t('ai.loading.youtube3.subtitle')
            },
            {
                icon: icon_ai_text,
                title: t('ai.loading.youtube4.title'),
                sub: t('ai.loading.youtube4.subtitle')
            }
        ],
        social: [
            {
                icon: icon_ai_main,
                title: t('ai.loading.social1.title'),
                sub: t('ai.loading.social1.subtitle')
            },
            {
                icon: icon_ai_eye,
                title: t('ai.loading.social2.title'),
                sub: t('ai.loading.social2.subtitle')
            },
            {
                icon: icon_ai_pencil,
                title: t('ai.loading.social3.title'),
                sub: t('ai.loading.social3.subtitle')
            },
            {
                icon: icon_ai_image,
                title: t('ai.loading.social4.title'),
                sub: t('ai.loading.social4.subtitle')
            },
            {
                icon: icon_ai_text,
                title: t('ai.loading.social5.title'),
                sub: t('ai.loading.social5.subtitle')
            }
        ]
    };

    const [currentIndex, setCurrentIndex] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentIndex(prev => {
                const length =
                    messages?.[type]?.length ??
                    messages?.website?.length ??
                    0;

                return length ? (prev + 1) % length : 0;
            });
        }, 3800);

        return () => clearInterval(interval);
    }, []);

    const current = messages?.[type]?.[currentIndex] ?? messages?.website?.[currentIndex];

    return (
        <Box mb="lg">
            <Center c="smartico-pink">
                <Image src={current.icon} w={88} h={88} />
            </Center>
            <Center>
                <Text fz={20} ta="center" fw={600} w={420}>{current.title}</Text>
            </Center>
            <Center mt="sm">
                <Text fz={16} ta="center" fw={400} w={460}>{current.sub}</Text>
            </Center>
        </Box>
    )
}